import { useEffect, useState, ReactNode } from 'react'
import { Navigate } from 'react-router'
import { getFirestore, doc, getDoc } from 'firebase/firestore'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import { useSessionStore } from '../../state'
import LoadingScreen from '../LoadingScreen'

interface AdminGuardProps {
  children: ReactNode
}

/**
 * AdminGuard wrapper component:
 * - Redirects unauthenticated users to /signin
 * - Checks Firestore User_Document for role === "admin"
 * - If not admin, redirects to / with "Insufficient permissions" snackbar
 * - If admin, renders children
 */
const AdminGuard = ({ children }: AdminGuardProps) => {
  const user = useSessionStore(s => s.user)
  const isAuthenticating = useSessionStore(s => s.isAuthenticating)

  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)
  const [checking, setChecking] = useState(true)
  const [showPermissionError, setShowPermissionError] = useState(false)

  useEffect(() => {
    let cancelled = false

    const checkRole = async () => {
      if (!user) {
        setChecking(false)
        return
      }

      try {
        const userDoc = await getDoc(doc(getFirestore(), 'users', user.uid))
        const admin = userDoc.exists() && userDoc.data().role === 'admin'
        if (!cancelled) {
          setIsAdmin(admin)
          if (!admin) setShowPermissionError(true)
          setChecking(false)
        }
      } catch {
        if (!cancelled) {
          setIsAdmin(false)
          setShowPermissionError(true)
          setChecking(false)
        }
      }
    }

    checkRole()
    return () => {
      cancelled = true
    }
  }, [user])

  if (isAuthenticating || checking) return <LoadingScreen />
  if (!user) return <Navigate to='/signin' />
  if (isAdmin === false) {
    return (
      <>
        <Snackbar
          open={showPermissionError}
          autoHideDuration={6000}
          onClose={() => setShowPermissionError(false)}
        >
          <Alert severity='error' onClose={() => setShowPermissionError(false)}>
            Insufficient permissions
          </Alert>
        </Snackbar>
        <Navigate to='/' />
      </>
    )
  }

  return <>{children}</>
}

export default AdminGuard
